import { AnimatePresence, motion } from "framer-motion";
import { Phone } from "lucide-react";
import { useState } from "react";
import { Button, Form, InputGroup, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { forgotPassword, isValidBdPhone } from "../../../services/auth.api";
import ResetPasswordForm from "./ResetPasswordForm";

/**
 * Two steps, not three: the API sends a code to the phone, then takes that
 * code and the new password together in a single reset call — there is no
 * intermediate "verify OTP for a reset token" round trip any more.
 */
const ForgetPasswordForm = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState("identifier"); // identifier -> reset
  const [phone, setPhone] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm({ mode: "all", defaultValues: { phone: "" } });

  const containerVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.4 } },
    exit: { opacity: 0, x: -50, transition: { duration: 0.4 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.3 } }),
  };

  const primaryColor = "#1B6DC1";

  const onSubmit = async (data) => {
    try {
      await forgotPassword({ phone: data.phone });
      setPhone(data.phone);
      setStep("reset");
    } catch (error) {
      setError("root", { message: error?.message || "Failed to send reset OTP" });
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="d-flex flex-column p-1 p-lg-4 gap-2"
    >
      <AnimatePresence mode="wait">
        {step === "identifier" && (
          <motion.div
            key="identifier"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="d-flex flex-column gap-2"
          >
            <motion.div custom={1} variants={itemVariants}>
              <h2 className="text-center fw-bold text-dark" style={{ fontSize: "1.875rem" }}>
                FORGOT PASSWORD
              </h2>
              <p className="text-center text-secondary mt-2">
                Enter your phone number and we'll send you a code to reset your password.
              </p>
            </motion.div>

            <motion.form
              custom={2}
              variants={itemVariants}
              onSubmit={handleSubmit(onSubmit)}
              className="d-flex flex-column gap-2"
            >
              {errors.root && (
                <div className="alert alert-danger py-2 mb-0" role="alert">
                  {errors.root.message}
                </div>
              )}

              <div>
                <InputGroup>
                  <InputGroup.Text
                    className={`bg-white border-end-0 rounded-start-pill ps-4 ${errors.phone ? "border-danger" : ""}`}
                  >
                    <Phone size={20} className="text-secondary" />
                  </InputGroup.Text>
                  <Form.Control
                    type="tel"
                    placeholder="01712345678"
                    {...register("phone", {
                      required: "Phone number is required",
                      validate: (v) =>
                        isValidBdPhone(v) ||
                        "Enter a valid Bangladeshi phone number (e.g. 01712345678)",
                    })}
                    className={`border-start-0 rounded-end-pill py-2 shadow-none ${errors.phone ? "border-danger" : ""}`}
                    disabled={isSubmitting}
                  />
                </InputGroup>
                {errors.phone && (
                  <small className="text-danger d-block mt-1 ms-1">{errors.phone.message}</small>
                )}
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="mt-3 py-2 rounded-pill fw-bold text-white d-flex align-items-center justify-content-center gap-2"
                style={{ backgroundColor: primaryColor, border: "none" }}
              >
                Send Code
                {isSubmitting && <Spinner animation="border" size="sm" />}
              </Button>
            </motion.form>

            <motion.div custom={3} variants={itemVariants} className="text-center mt-3">
              <p className="text-secondary">
                Remember your password?{" "}
                <Button
                  variant="link"
                  className="p-0"
                  onClick={() => navigate("/authentication/login")}
                  style={{ color: primaryColor, textDecoration: "none" }}
                >
                  Sign In
                </Button>
              </p>
            </motion.div>
          </motion.div>
        )}

        {step === "reset" && (
          <motion.div
            key="reset"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <ResetPasswordForm phone={phone} onBack={() => setStep("identifier")} />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ForgetPasswordForm;
